export interface CatchRecord {
  id: string;
  species: string;
  lengthCm: number;
  keptOrReleased: string | null;
  status: string;
  submissionTimestamp: string | null;
  eventId: string;
  competitorName: string;
}

export interface SeasonStats {
  totalCatches: number;
  longestCatch: { species: string; lengthCm: number } | null;
  competitionsEntered: number;
  keptPercent: number | null;
  releasedPercent: number | null;
}

export interface BadgeState {
  firstCatch: boolean;
  tenFishClub: boolean;
  allRounder: boolean;
  catchAndReleaseHero: boolean;
  competitionRegular: boolean;
  seasonChampion: boolean;
  goldPlace: boolean;
  silverPlace: boolean;
  bronzePlace: boolean;
  bigFish: boolean;
  hatTrick: boolean;
  centuryClub: boolean;
}

const SPECIES_LABELS: Record<string, string> = {
  FLATHEAD: 'Flathead',
  BREAM: 'Bream',
  JEWFISH: 'Jewfish',
  MANGROVE_JACK: 'Mangrove Jack',
  WHITING: 'Whiting',
  TAILOR: 'Tailor',
  TREVALLY: 'Trevally',
  OTHER: 'Other',
};

export function speciesLabel(species: string): string {
  return SPECIES_LABELS[species] || species;
}

export function ordinal(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return `${n}st`;
  if (mod10 === 2 && mod100 !== 12) return `${n}nd`;
  if (mod10 === 3 && mod100 !== 13) return `${n}rd`;
  return `${n}th`;
}

export function filterSeasonCatches(catches: CatchRecord[], year: number): CatchRecord[] {
  const start = new Date(`${year}-01-01T00:00:00Z`).getTime();
  const end = new Date(`${year}-12-31T23:59:59.999Z`).getTime();
  return catches.filter((c) => {
    if (c.status !== 'APPROVED' || !c.submissionTimestamp) return false;
    const t = new Date(c.submissionTimestamp).getTime();
    return t >= start && t <= end;
  });
}

export function computeSeasonStats(seasonCatches: CatchRecord[]): SeasonStats {
  if (seasonCatches.length === 0) {
    return { totalCatches: 0, longestCatch: null, competitionsEntered: 0, keptPercent: null, releasedPercent: null };
  }

  const longest = seasonCatches.reduce((max, c) => (c.lengthCm > max.lengthCm ? c : max), seasonCatches[0]);
  const eventIds = new Set(seasonCatches.map((c) => c.eventId));
  const withStatus = seasonCatches.filter((c) => c.keptOrReleased != null);

  let keptPercent: number | null = null;
  let releasedPercent: number | null = null;
  if (withStatus.length > 0) {
    const kept = withStatus.filter((c) => c.keptOrReleased === 'KEPT').length;
    keptPercent = Math.round((kept / withStatus.length) * 100);
    releasedPercent = 100 - keptPercent;
  }

  return {
    totalCatches: seasonCatches.length,
    longestCatch: { species: longest.species, lengthCm: longest.lengthCm },
    competitionsEntered: eventIds.size,
    keptPercent,
    releasedPercent,
  };
}

export function computeBadgeStates(
  allApprovedCatches: CatchRecord[],
  placements: Array<{ species: string; position: number }>
): BadgeState {
  const speciesSet = new Set(allApprovedCatches.map((c) => c.species));
  const eventIds = new Set(allApprovedCatches.map((c) => c.eventId));
  const withStatus = allApprovedCatches.filter((c) => c.keptOrReleased != null);
  const released = withStatus.filter((c) => c.keptOrReleased === 'RELEASED').length;

  // Hat Trick: 1st place in 3 or more different species categories
  const firstPlaceSpecies = new Set(placements.filter((p) => p.position === 1).map((p) => p.species));

  // Big Fish: any single catch >= 50cm
  const bigFish = allApprovedCatches.some((c) => c.lengthCm >= 50);

  return {
    firstCatch: allApprovedCatches.length >= 1,
    tenFishClub: allApprovedCatches.length >= 10,
    centuryClub: allApprovedCatches.length >= 100,
    allRounder: ['FLATHEAD', 'BREAM', 'JEWFISH', 'MANGROVE_JACK', 'WHITING', 'TAILOR', 'TREVALLY'].every((s) => speciesSet.has(s)),
    catchAndReleaseHero: withStatus.length > 0 && released / withStatus.length >= 0.8,
    competitionRegular: eventIds.size >= 5,
    seasonChampion: placements.some((p) => p.position <= 3),
    goldPlace: placements.some((p) => p.position === 1),
    silverPlace: placements.some((p) => p.position === 2),
    bronzePlace: placements.some((p) => p.position === 3),
    bigFish,
    hatTrick: firstPlaceSpecies.size >= 3,
  };
}

export function computePersonalSpeciesBests(catches: CatchRecord[]): Array<{ species: string; lengthCm: number }> {
  const best: Record<string, number> = {};
  for (const c of catches) {
    if (!best[c.species] || c.lengthCm > best[c.species]) best[c.species] = c.lengthCm;
  }
  return Object.entries(best).map(([species, lengthCm]) => ({ species, lengthCm }));
}